import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useQueries } from '@tanstack/react-query';
import { Icon } from '@iconify/react';
import chevronDown from '@iconify/icons-mdi/chevron-down';
import chevronUp from '@iconify/icons-mdi/chevron-up';

import type { Channel } from 'stream-chat';

import { getBookingSession } from '@/features/bookings-management/api';
import type { BookingSession } from '@/features/bookings-management/types';
import {
  getChannelMetadata,
  getRecord,
  parseDateCandidate,
  resolveBookingSessionId,
  sortDatedChannels,
  type DatedChannelSort,
  type StableDatedChannel,
} from '../../utils/channelOrdering';
import { JobSupportSessionsProvider } from './JobSupportSessionContext';

import type { StreamChatType } from '../../types';

type JobSupportChannelGroupsProps = {
  channels: Channel<StreamChatType>[];
  renderChannel: (channel: Channel<StreamChatType>) => ReactNode;
};

type JobSupportGroupId =
  | 'needs-response'
  | 'overdue'
  | 'today'
  | 'tomorrow'
  | 'upcoming'
  | 'completed'
  | 'archived';

type GroupedJobSupportChannel = StableDatedChannel;

type JobSupportGroup = {
  defaultCollapsed?: boolean;
  id: JobSupportGroupId;
  label: string;
  sort: DatedChannelSort;
};

const ARCHIVE_AFTER_DAYS = 2;
const TERMINAL_STATUSES = [
  'completed',
  'done',
  'finished',
  'cancelled',
  'canceled',
];

const JOB_SUPPORT_GROUPS: JobSupportGroup[] = [
  { id: 'needs-response', label: 'Needs response', sort: 'activity-desc' },
  { id: 'overdue', label: 'Overdue', sort: 'date-desc' },
  { id: 'today', label: 'Today', sort: 'date-asc' },
  {
    id: 'tomorrow',
    label: 'Tomorrow',
    sort: 'date-asc',
    defaultCollapsed: true,
  },
  {
    id: 'upcoming',
    label: 'Upcoming',
    sort: 'date-asc',
    defaultCollapsed: true,
  },
  {
    id: 'completed',
    label: 'Completed',
    sort: 'date-desc',
    defaultCollapsed: true,
  },
  {
    id: 'archived',
    label: 'Archived',
    sort: 'date-desc',
    defaultCollapsed: true,
  },
];

const getMetadataValue = (
  metadata: Record<string, unknown>,
  keys: string[]
): unknown => {
  for (const key of keys) {
    const value = metadata[key];
    if (value !== undefined && value !== null && String(value).trim()) {
      return value;
    }
  }

  return null;
};

const normalizeStatus = (value: unknown): string => {
  const record = getRecord(value);
  const rawValue =
    record.value ?? record.display ?? record.name ?? record.status ?? value;

  return String(rawValue ?? '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ');
};

const isTerminalStatus = (
  metadata: Record<string, unknown>,
  session?: BookingSession
): boolean => {
  const statusValue = getMetadataValue(metadata, [
    'bookingSessionStatus',
    'booking_session_status',
    'sessionStatus',
    'session_status',
    'bookingStatus',
    'booking_status',
    'status',
    'statusValue',
    'statusDisplay',
  ]) ?? session?.booking_session_status;
  const normalizedStatus = normalizeStatus(statusValue);

  return TERMINAL_STATUSES.some((status) => normalizedStatus.includes(status));
};

const isArchivedChannel = (metadata: Record<string, unknown>): boolean =>
  Boolean(
    getMetadataValue(metadata, [
      'isArchived',
      'is_archived',
      'archivedAt',
      'archived_at',
    ])
  );

const applyTimeToDate = (date: Date, timeValue: unknown): Date => {
  if (!timeValue) return date;

  const timeText = String(timeValue).trim();
  const match = timeText.match(/^(\d{1,2}):(\d{2})\s*([ap]m)?$/i);
  if (!match) return date;

  const [, hourValue, minuteValue, meridiemValue] = match;
  const meridiem = meridiemValue?.toLowerCase();
  let hours = Number(hourValue);
  const minutes = Number(minuteValue);

  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return date;
  if (meridiem === 'pm' && hours < 12) hours += 12;
  if (meridiem === 'am' && hours === 12) hours = 0;

  const dateWithTime = new Date(date);
  dateWithTime.setHours(hours, minutes, 0, 0);
  return dateWithTime;
};

const startOfLocalDay = (date: Date): Date => {
  const day = new Date(date);
  day.setHours(0, 0, 0, 0);
  return day;
};

const getDaysFromToday = (date: Date): number => {
  const today = startOfLocalDay(new Date());
  const targetDate = startOfLocalDay(date);
  return Math.round(
    (targetDate.getTime() - today.getTime()) / (24 * 60 * 60 * 1000)
  );
};

const getSessionDate = (
  metadata: Record<string, unknown>,
  session?: BookingSession
): { date: Date | null; hasDate: boolean; sortTime: number } => {
  const dateValue =
    getMetadataValue(metadata, ['bookingDate', 'date']) ?? session?.date;
  const timeValue =
    getMetadataValue(metadata, ['bookingTime', 'time']) ?? session?.time;
  const parsedDate = parseDateCandidate(dateValue);

  if (!parsedDate) {
    return { date: null, hasDate: false, sortTime: Number.MAX_SAFE_INTEGER };
  }

  const date = applyTimeToDate(parsedDate, timeValue);
  return { date, hasDate: true, sortTime: date.getTime() };
};

const getGroupId = (
  channel: Channel<StreamChatType>,
  metadata: Record<string, unknown>,
  session: BookingSession | undefined,
  sessionDate: Date | null
): JobSupportGroupId => {
  if (isArchivedChannel(metadata)) return 'archived';
  if (channel.countUnread() > 0) return 'needs-response';
  if (!sessionDate) return 'upcoming';

  const daysFromToday = getDaysFromToday(sessionDate);
  const terminalStatus = isTerminalStatus(metadata, session);

  if (terminalStatus) {
    return daysFromToday < -ARCHIVE_AFTER_DAYS ? 'archived' : 'completed';
  }

  if (daysFromToday === 0) return 'today';
  if (daysFromToday === 1) return 'tomorrow';
  if (daysFromToday < 0) return 'overdue';

  return 'upcoming';
};

const sortGroupedChannels = (
  channels: GroupedJobSupportChannel[],
  sort: JobSupportGroup['sort']
) => sortDatedChannels(channels, sort);

const hasCompleteMetadata = (metadata: Record<string, unknown>) =>
  Boolean(
    getMetadataValue(metadata, ['bookingDate', 'date']) &&
      getMetadataValue(metadata, [
        'bookingSessionStatus',
        'booking_session_status',
        'sessionStatus',
        'session_status',
        'bookingStatus',
        'booking_status',
        'status',
        'statusValue',
        'statusDisplay',
      ])
  );

const hasStyledBookingSessionStatus = (metadata: Record<string, unknown>) => {
  const statusValue = getMetadataValue(metadata, [
    'bookingSessionStatus',
    'booking_session_status',
    'sessionStatus',
    'session_status',
    'bookingStatus',
    'booking_status',
    'status',
    'statusValue',
    'statusDisplay',
  ]);
  const statusRecord = getRecord(statusValue);
  const styleClass = statusRecord.style_class ?? statusRecord.styleClass;

  return Boolean(styleClass && String(styleClass).trim());
};

const getSessionIdsToFetch = (channels: Channel<StreamChatType>[]) => {
  const sessionIds = channels.reduce<number[]>((acc, channel) => {
    const metadata = getChannelMetadata(channel);
    if (hasCompleteMetadata(metadata) && hasStyledBookingSessionStatus(metadata))
      return acc;

    const sessionId = resolveBookingSessionId(channel);
    if (sessionId && !acc.includes(sessionId)) acc.push(sessionId);

    return acc;
  }, []);

  return sessionIds;
};

const groupJobSupportChannels = (
  channels: Channel<StreamChatType>[],
  sessionsById: Map<number, BookingSession>
) => {
  const groupedChannels = JOB_SUPPORT_GROUPS.reduce<
    Record<JobSupportGroupId, GroupedJobSupportChannel[]>
  >(
    (acc, group) => ({
      ...acc,
      [group.id]: [],
    }),
    {} as Record<JobSupportGroupId, GroupedJobSupportChannel[]>
  );

  channels.forEach((channel) => {
    const metadata = getChannelMetadata(channel);
    const sessionId = resolveBookingSessionId(channel);
    const session = sessionId ? sessionsById.get(sessionId) : undefined;
    const { date, hasDate, sortTime } = getSessionDate(metadata, session);
    const groupId = getGroupId(channel, metadata, session, date);

    groupedChannels[groupId].push({ channel, hasDate, sortTime });
  });

  return groupedChannels;
};

const JobSupportChannelGroupSection = ({
  group,
  items,
  renderChannel,
}: {
  group: JobSupportGroup;
  items: GroupedJobSupportChannel[];
  renderChannel: (channel: Channel<StreamChatType>) => ReactNode;
}) => {
  const [isCollapsed, setIsCollapsed] = useState(
    Boolean(group.defaultCollapsed)
  );

  if (!items.length) return null;

  return (
    <div
      className={`job-support-channel-group job-support-channel-group--${group.id}`}
    >
      <button
        aria-expanded={!isCollapsed}
        className="job-support-channel-group__header"
        onClick={() => setIsCollapsed((current) => !current)}
        type="button"
      >
        <Icon icon={isCollapsed ? chevronDown : chevronUp} />
        <span className="job-support-channel-group__title">{group.label}</span>
        <span className="job-support-channel-group__count">{items.length}</span>
      </button>
      {!isCollapsed && (
        <div className="job-support-channel-group__items">
          {items.map(({ channel }) => (
            <div
              className="job-support-channel-group__item"
              key={channel.cid || channel.id}
            >
              {renderChannel(channel)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const JobSupportChannelGroups = ({
  channels,
  renderChannel,
}: JobSupportChannelGroupsProps) => {
  const sessionIdsToFetch = useMemo(
    () => getSessionIdsToFetch(channels),
    [channels]
  );
  const sessionQueries = useQueries({
    queries: sessionIdsToFetch.map((sessionId) => ({
      queryKey: ['chatJobSupportGroupSession', sessionId],
      queryFn: () => getBookingSession(sessionId),
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: 5 * 60 * 1000,
    })),
  });
  const sessionsById = useMemo(() => {
    return sessionQueries.reduce<Map<number, BookingSession>>(
      (acc, query, index) => {
        const session = query.data as BookingSession | undefined;
        if (session) acc.set(sessionIdsToFetch[index], session);
        return acc;
      },
      new Map()
    );
  }, [sessionIdsToFetch, sessionQueries]);
  const groupedChannels = useMemo(
    () => groupJobSupportChannels(channels, sessionsById),
    [channels, sessionsById]
  );

  return (
    <JobSupportSessionsProvider sessionsById={sessionsById}>
      <div className="job-support-channel-groups">
        {JOB_SUPPORT_GROUPS.map((group) => (
          <JobSupportChannelGroupSection
            group={group}
            items={sortGroupedChannels(groupedChannels[group.id], group.sort)}
            key={group.id}
            renderChannel={renderChannel}
          />
        ))}
      </div>
    </JobSupportSessionsProvider>
  );
};
